/**
 * @file Starts the `queryAnalytics` callable for one request and returns its
 * answer from `analyticsAnswers`. Consumers: the Analytics tab panels. Two
 * effects split the entry from the run. The entry effect follows the request's
 * key alone (query, scope, period, a series' grain or group): it sets the
 * entry to loading, and its cleanup deletes it, so a request change shows
 * loading and no render ever acts on an answer the current mount did not
 * request (a refusal from an earlier visit or another user is never read
 * back). The run effect also follows `analyticsAnswersRefetch.computedAt`, so
 * a refetch starts the callable while the shown answer stays on screen until
 * the new one lands, keeping the page height and scroll position. Its cleanup
 * aborts the run, and a response is written only while its run is not
 * aborted, so a stale response never overwrites a newer one for the same key.
 * A response `analyticsAnswerUnchanged` finds equal to the shown one is not
 * written, so nothing re-renders, and a failed response never replaces a
 * shown answered one: the panel keeps its numbers and the next cycle tries
 * again. A failure over loading, a refusal or another failure is still shown.
 * Grain and group are sent only when the request carries them, since the
 * service refuses a key the query does not declare.
 */
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import type { Snapshot } from 'valtio';

import { queryAnalytics } from '@/api/analytics/services';
import type { NamedQueryRequest } from '#types';

import { analyticsAnswerKey } from './analyticsAnswerKey';
import { analyticsAnswers } from './analyticsAnswers';
import { analyticsAnswersRefetch } from './analyticsAnswersRefetch';
import { analyticsAnswerUnchanged } from './analyticsAnswerUnchanged';
import type { AnalyticsAnswer } from './index.types';

export const useAnalyticsAnswer = (
  request: NamedQueryRequest,
): Snapshot<AnalyticsAnswer> | undefined => {
  const {
    query,
    level,
    parameters: { period, grain, group },
  } = request;
  const key = analyticsAnswerKey(request);
  const { computedAt } = useSnapshot(analyticsAnswersRefetch);

  useEffect(() => {
    analyticsAnswers[key] = { status: 'loading' };
    return () => {
      delete analyticsAnswers[key];
    };
  }, [key]);

  useEffect(() => {
    const started: NamedQueryRequest = {
      query,
      parameters: {
        period,
        ...(grain === undefined ? {} : { grain }),
        ...(group === undefined ? {} : { group }),
      },
      level,
    };
    const startedKey = analyticsAnswerKey(started);
    const controller = new AbortController();
    void queryAnalytics(started).then((answer) => {
      const shown = analyticsAnswers[startedKey];
      const keepsShown =
        analyticsAnswerUnchanged(shown, answer) ||
        (shown?.status === 'answered' && answer.status === 'failed');
      if (!controller.signal.aborted && !keepsShown)
        analyticsAnswers[startedKey] = answer;
    });
    return () => {
      controller.abort();
    };
  }, [query, level, period, grain, group, computedAt]);

  return useSnapshot(analyticsAnswers)[key];
};
